/**
 * DAS Power Detector Chart Component
 * 
 * Specialized chart component for DAS power detector visualization with:
 * - Dynamic Y-axis range calculated from actual data per device
 * - Special value handling: ≤-200 displayed as "No Signal"
 * - Visual threshold indicators calculated from data max value
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, IconButton, CircularProgress, Alert, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, FormControl, InputLabel, Select, SelectChangeEvent } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { selectedDeviceSelector } from '../../../../store/api/devices/selectors';
import { IFullDeviceDetails } from '../../../../store/api/deviceConfig/types';
import { ChartColors, ChartColorName, getChartColor } from '../../../../theme/chartColors';
import { useTheme } from '../../../../theme/hooks';
import { LOCAL_STORAGE } from '../../../../globals/globals';
import { SiteChartsConfig } from '../../../SiteCharts/SiteCharts';

dayjs.extend(utc);

type TimeRange = 'lastHour' | 'lastDay' | 'lastWeek' | 'lastMonth' | 'all' | 'custom';

interface DasPowerDetectorChartProps {
  config: SiteChartsConfig;
  onFullscreenToggle: () => void;
  isFullscreen: boolean;
  additionalParams?: Record<string, string>;
}

const DasPowerDetectorChart: React.FC<DasPowerDetectorChartProps> = ({ 
  config,
  onFullscreenToggle, 
  isFullscreen,
  additionalParams
}) => {
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  
  // Local filter state for this chart
  const [timeRange, setTimeRange] = useState<TimeRange>('lastWeek');
  const [truncate, setTruncate] = useState<string>(''); // Empty string = no aggregation
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { mode } = useTheme();
  const getCssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [timeRangeMenuAnchor, setTimeRangeMenuAnchor] = useState<null | HTMLElement>(null);
  const [aggregationMenuAnchor, setAggregationMenuAnchor] = useState<null | HTMLElement>(null);

  // Memoize additionalParams to prevent unnecessary re-fetches
  const stableAdditionalParams = useMemo(() => 
    JSON.stringify(additionalParams || {}), 
    [additionalParams]
  );
  
  // Memoize config values that shouldn't trigger re-fetches
  const apiEndpoint = useMemo(() => config.apiEndpoint, [config.apiEndpoint]);
  
  const storageKey = `chart-view-${config.title}`;
  const [showTable, setShowTable] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(storageKey);
    return stored === 'true';
  });
  
  const chartRef = useRef<ReactECharts>(null);
  const menuOpen = Boolean(anchorEl);
  const timeRangeMenuOpen = Boolean(timeRangeMenuAnchor);
  const aggregationMenuOpen = Boolean(aggregationMenuAnchor);
  const menuLayerSx = {
    zIndex: 1700,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTimeRangeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTimeRangeMenuAnchor(event.currentTarget);
    handleMenuClose();
  };

  const handleTimeRangeMenuClose = () => {
    setTimeRangeMenuAnchor(null);
  };

  const handleAggregationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAggregationMenuAnchor(event.currentTarget);
    handleMenuClose();
  };

  const handleAggregationMenuClose = () => {
    setAggregationMenuAnchor(null);
  };

  const handleTimeRangeSelect = (value: TimeRange) => {
    setTimeRange(value);
    if (value !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
    handleTimeRangeMenuClose();
  };

  const handleAggregationSelect = (value: string) => {
    setTruncate(value);
    handleAggregationMenuClose();
  };

  const handleToggleView = () => {
    const newValue = !showTable;
    setShowTable(newValue);
    sessionStorage.setItem(storageKey, String(newValue));
    handleMenuClose();
  };

  /**
   * Format power value for display
   * Shows raw value with appropriate label
   */
  const formatPowerValue = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return 'N/A';
    
    // Apply divisor if configured
    const scaledValue = config.divisor ? value / config.divisor : value;
    
    // Show label based on scaled value against thresholds
    const noSignalThreshold = config.yAxes[0]?.min ?? -200;
    const overpowerThreshold = config.yAxes[0]?.max ?? 0;
    
    if (scaledValue < noSignalThreshold) return 'No Signal';
    if (scaledValue > overpowerThreshold) return 'Overpower';
    
    return scaledValue.toLocaleString();
  };

  const handleExportCSV = () => {
    if (chartData.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = ['Timestamp', ...config.tableColumns.map(col => col.header)];
    
    const rows = chartData.map(d => {
      const row = [dayjs.utc(d.timestamp).format('YYYY-MM-DD HH:mm:ss')];
      config.tableColumns.forEach(col => {
        const value = d[col.dataKey];
        row.push(formatPowerValue(value));
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${config.csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleMenuClose();
  };

  const handleSaveAsImage = () => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (chartInstance) {
      const url = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: ChartColors.background
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.chartImageFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.png`;
      link.click();
    }
    handleMenuClose();
  };

  useEffect(() => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
    console.log(`🔄 [${timestamp}] [DasPowerDetectorChart] useEffect triggered for "${config.title}"`, {
      serialNumber: selectedDevice?.serialNumber,
      timeRange,
      truncate,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      apiEndpoint: apiEndpoint,
      additionalParams: stableAdditionalParams,
      refreshTrigger
    });

    const fetchChartData = async () => {
      if (!selectedDevice?.serialNumber) {
        setError('No device selected');
        setLoading(false);
        return;
      }

      if (timeRange === 'custom' && (!startDate || !endDate)) {
        return;
      }

      const fetchTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      console.log(`📡 [${fetchTimestamp}] [DasPowerDetectorChart] Starting fetch for "${config.title}"`);

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {
          site_name: selectedDevice.serialNumber,
        };
        
        // Only add truncate if it's set (not empty)
        if (truncate) {
          params.truncate = truncate;
        }

        const parsedAdditionalParams = JSON.parse(stableAdditionalParams);
        if (parsedAdditionalParams && Object.keys(parsedAdditionalParams).length > 0) {
          Object.assign(params, parsedAdditionalParams);
        }

        if (timeRange === 'custom' && startDate && endDate) {
          params.start_date = startDate.startOf('day').format('YYYY-MM-DD');
          params.end_date = endDate.startOf('day').format('YYYY-MM-DD');
        } else {
          params.time_range = timeRange;
        }

        const apiBaseUrl = '/v1/charts';
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        
        const response = await fetch(`${apiBaseUrl}${apiEndpoint}?${new URLSearchParams(params)}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        const completeTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
        console.log(`✅ [${completeTimestamp}] [DasPowerDetectorChart] Fetch completed for "${config.title}", received ${data.length} records`);
        
        setChartData(data);
      } catch (err) {
        const errorTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
        console.error(`❌ [${errorTimestamp}] [DasPowerDetectorChart] Fetch error for "${config.title}":`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedDevice?.serialNumber, timeRange, truncate, startDate, endDate, apiEndpoint, stableAdditionalParams, refreshTrigger]);

  // Auto-refresh charts every 5 minutes
  useEffect(() => {
    const chartAutoRefreshInterval = window.setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 300000); // 5 minutes

    return () => {
      window.clearInterval(chartAutoRefreshInterval);
    };
  }, []);

  const chartHeight = isFullscreen 
    ? Math.min(window.innerHeight - 350, 600)
    : 250;

  // Configured thresholds for "No Signal" and "Overpower" labels
  const noSignalThreshold = config.yAxes[0]?.min ?? -200;
  const overpowerThreshold = config.yAxes[0]?.max ?? 0;

  // Calculate dynamic Y-axis range based on actual data values
  const calculateYAxisRange = () => {
    if (chartData.length === 0) {
      return { yAxisMin: noSignalThreshold, yAxisMax: overpowerThreshold };
    }

    let minValue = Infinity;
    let maxValue = -Infinity;

    // Find min/max across all series
    config.series.forEach((seriesConfig) => {
      chartData.forEach((d) => {
        const value = d[seriesConfig.dataKey];
        if (value !== undefined && value !== null) {
          // Apply divisor if configured
          const scaledValue = config.divisor ? value / config.divisor : value;
          
          // Only consider values within the valid range (not clamped)
          if (scaledValue >= noSignalThreshold && scaledValue <= overpowerThreshold) {
            minValue = Math.min(minValue, scaledValue);
            maxValue = Math.max(maxValue, scaledValue);
          }
        }
      });
    });

    // If no valid data found, use thresholds
    if (!isFinite(minValue) || !isFinite(maxValue)) {
      return { yAxisMin: noSignalThreshold, yAxisMax: overpowerThreshold };
    }

    // Add 10% padding to the range for better visualization
    const range = maxValue - minValue;
    const padding = Math.max(range * 0.1, 5); // At least 5 dBm padding
    
    const calculatedMin = Math.max(noSignalThreshold, Math.floor(minValue - padding));
    const calculatedMax = Math.min(overpowerThreshold, Math.ceil(maxValue + padding));

    return { yAxisMin: calculatedMin, yAxisMax: calculatedMax };
  };

  const { yAxisMin, yAxisMax } = calculateYAxisRange();

  const getOption = () => {
    if (chartData.length === 0) return {};

    const timestamps = chartData.map(d => dayjs.utc(d.timestamp).format('MMM DD HH:mm'));
    
    const resolveColor = (color: ChartColorName | string): string => {
      if (color in ChartColors) {
        return getChartColor(color as ChartColorName);
      }
      return color;
    };
    
    const seriesData = config.series.map((seriesConfig, index) => {
      const resolvedColor = resolveColor(seriesConfig.color);
      
      return {
        name: seriesConfig.name,
        type: seriesConfig.type,
        yAxisIndex: seriesConfig.yAxisIndex,
        data: chartData.map(d => {
          const value = d[seriesConfig.dataKey];
          // Handle missing values
          if (value === undefined || value === null) return null;
          
          // Apply divisor if configured
          const scaledValue = config.divisor ? value / config.divisor : value;
          
          // CLAMP values to threshold range
          // Values below threshold -> plot at yAxisMin (will show as "No Signal" in tooltip)
          // Values above threshold -> plot at yAxisMax (will show as "Overpower" in tooltip)
          if (scaledValue < noSignalThreshold) return yAxisMin;
          if (scaledValue > overpowerThreshold) return yAxisMax;
          
          return scaledValue;
        }),
        itemStyle: {
          color: resolvedColor
        },
        ...(seriesConfig.type === 'line' ? {
          smooth: false,
          connectNulls: false,  // Break the line at null values instead of connecting them
          lineStyle: {
            color: resolvedColor,
            width: 2
          },
          symbol: isFullscreen ? 'circle' : 'none',
          symbolSize: isFullscreen ? 6 : 0,
          showSymbol: isFullscreen
        } : {}),
        emphasis: {
          focus: 'series'
        }
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        formatter: (params: any) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          
          // Get thresholds for comparison
          const noSignalThreshold = config.yAxes[0]?.min ?? -200;
          const overpowerThreshold = config.yAxes[0]?.max ?? 0;
          
          params.forEach((param: any) => {
            // Get the raw value from chartData before clamping
            const rawValue = chartData[param.dataIndex]?.[config.series[param.seriesIndex]?.dataKey];
            
            if (rawValue === undefined || rawValue === null) {
              return;
            }
            
            // Apply divisor if configured
            const scaledValue = config.divisor ? rawValue / config.divisor : rawValue;
            
            let displayValue: string;
            let unit = ' dBm';
            
            // Show appropriate label based on scaled value against thresholds
            if (scaledValue < noSignalThreshold) {
              displayValue = 'No Signal';
              unit = '';
            } else if (scaledValue > overpowerThreshold) {
              displayValue = 'Overpower';
              unit = '';
            } else {
              displayValue = scaledValue.toLocaleString();
            }
            
            result += `${param.marker} ${param.seriesName}: ${displayValue}${unit}<br/>`;
          });
          return result;
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: [0]  // Enable zoom on Y-axis (index 0)
          },
          restore: {}
        },
        iconStyle: {
          color: 'none',
          borderColor: getCssVar('--muted-foreground'),
          borderWidth: 1
        },
        emphasis: {
          iconStyle: {
            color: 'none',
            borderColor: getCssVar('--foreground'),
            borderWidth: 1
          }
        },
        right: 20,
        top: 5
      },
      legend: {
        data: config.series.map(s => s.name),
        top: 35,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
          color: getCssVar('--muted-foreground')
        }
      },
      grid: {
        left: 80,
        right: 80,
        bottom: isFullscreen ? 80 : 60,
        top: 70,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: timestamps,
        axisPointer: {
          type: 'shadow'
        },
        axisLine: {
          show: false  // Hide the X-axis line (which appears at y=0 on value charts)
        },
        axisLabel: {
          fontSize: 11,
          color: getCssVar('--muted-foreground'),
          rotate: 45
        }
      },
      yAxis: [{
        type: 'value',
        name: config.yAxes[0]?.name || 'Power (dBm)',
        position: 'left',
        nameLocation: 'middle',
        nameGap: 50,
        min: yAxisMin,
        max: yAxisMax,
        nameTextStyle: {
          fontSize: 12,
          color: getCssVar('--muted-foreground'),
          align: 'center'
        },
        nameRotate: 90,
        axisLabel: {
          formatter: (value: number) => {
            // Show "No Signal" when at minimum threshold
            if (value === yAxisMin) {
              return 'No Signal';
            }
            // Show "Overpower" when at maximum threshold
            if (value === yAxisMax) {
              return 'Overpower';
            }
            // Otherwise show numeric value
            return value.toString();
          },
          fontSize: 11,
          color: getCssVar('--muted-foreground'),
          showMinLabel: true,  // Always show min label
          showMaxLabel: true   // Always show max label
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: ChartColors.gridLine,
            opacity: 0.3
          }
        },
        axisLine: {
          show: false  // Hide the Y-axis line
        }
      }],
      series: seriesData,
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100
        },
        ...(isFullscreen ? [{
          type: 'slider' as const,
          start: 0,
          end: 100,
          height: 20,
          bottom: 10
        }] : [])
      ]
    };
  };

  return (
    <Box
      sx={{
        p: 2,
        height: isFullscreen ? Math.min(window.innerHeight - 250, 700) : 'auto',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
        <p className="text-sm font-semibold leading-none tracking-tight">
          {config.title}
        </p>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={onFullscreenToggle} size="small" sx={{ color: 'var(--muted-foreground)' }}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton 
            onClick={handleMenuOpen}
            size="small"
            aria-label="chart options"
            sx={{ color: 'var(--muted-foreground)' }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Only show custom date range picker when custom is selected */}
      {timeRange === 'custom' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 }
                }
              }}
              maxDate={endDate || dayjs()}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 }
                }
              }}
              minDate={startDate || undefined}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
          <IconButton 
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            size="small"
            sx={{ 
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              padding: '4px',
              width: '32px',
              height: '32px'
            }}
            title="Refresh now"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Menu
        id="chart-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        sx={menuLayerSx}
        slotProps={{ paper: { sx: menuLayerSx } }}
      >
        <MenuItem onClick={handleTimeRangeMenuOpen}>
          <ShowChartIcon sx={{ mr: 1, fontSize: 20 }} />
          Time Range: {timeRange === 'lastHour' ? 'Last Hour' : timeRange === 'lastDay' ? 'Last Day' : timeRange === 'lastWeek' ? 'Last Week' : timeRange === 'lastMonth' ? 'Last Month' : timeRange === 'all' ? 'All' : 'Custom'}
        </MenuItem>
        <MenuItem onClick={handleAggregationMenuOpen}>
          <TableChartIcon sx={{ mr: 1, fontSize: 20 }} />
          Aggregation: {truncate === '' ? 'None' : truncate === 'minute' ? 'Minute' : truncate === 'hour' ? 'Hour' : 'Day'}
        </MenuItem>
        <MenuItem onClick={handleToggleView}>
          {showTable ? <ShowChartIcon sx={{ mr: 1, fontSize: 20 }} /> : <TableChartIcon sx={{ mr: 1, fontSize: 20 }} />}
          {showTable ? 'Show as Chart' : 'Show as Table'}
        </MenuItem>
        <MenuItem onClick={handleExportCSV} disabled={chartData.length === 0}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleSaveAsImage} disabled={chartData.length === 0 || showTable}>
          <ImageIcon sx={{ mr: 1, fontSize: 20 }} />
          Save as Image
        </MenuItem>
      </Menu>

      {/* Time Range Submenu */}
      <Menu
        id="time-range-menu"
        anchorEl={timeRangeMenuAnchor}
        open={timeRangeMenuOpen}
        onClose={handleTimeRangeMenuClose}
        sx={menuLayerSx}
        slotProps={{ paper: { sx: menuLayerSx } }}
      >
        <MenuItem onClick={() => handleTimeRangeSelect('lastHour')} selected={timeRange === 'lastHour'}>
          Last Hour
        </MenuItem>
        <MenuItem onClick={() => handleTimeRangeSelect('lastDay')} selected={timeRange === 'lastDay'}>
          Last Day
        </MenuItem>
        <MenuItem onClick={() => handleTimeRangeSelect('lastWeek')} selected={timeRange === 'lastWeek'}>
          Last Week
        </MenuItem>
        <MenuItem onClick={() => handleTimeRangeSelect('lastMonth')} selected={timeRange === 'lastMonth'}>
          Last Month
        </MenuItem>
        <MenuItem onClick={() => handleTimeRangeSelect('all')} selected={timeRange === 'all'}>
          All
        </MenuItem>
        <MenuItem onClick={() => handleTimeRangeSelect('custom')} selected={timeRange === 'custom'}>
          Custom Range
        </MenuItem>
      </Menu>

      {/* Aggregation Submenu */}
      <Menu
        id="aggregation-menu"
        anchorEl={aggregationMenuAnchor}
        open={aggregationMenuOpen}
        onClose={handleAggregationMenuClose}
        sx={menuLayerSx}
        slotProps={{ paper: { sx: menuLayerSx } }}
      >
        <MenuItem onClick={() => handleAggregationSelect('')} selected={truncate === ''}>
          None
        </MenuItem>
        <MenuItem onClick={() => handleAggregationSelect('minute')} selected={truncate === 'minute'}>
          Minute
        </MenuItem>
        <MenuItem onClick={() => handleAggregationSelect('hour')} selected={truncate === 'hour'}>
          Hour
        </MenuItem>
        <MenuItem onClick={() => handleAggregationSelect('day')} selected={truncate === 'day'}>
          Day
        </MenuItem>
      </Menu>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && chartData.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>
            No data available for selected time range
          </Typography>
        </Box>
      )}

      {!loading && !error && chartData.length > 0 && !showTable && (
        <ReactECharts
          ref={chartRef}
          option={getOption()}
          style={{ height: `${chartHeight}px`, width: '100%' }}
          notMerge={true}
          lazyUpdate={true}
          theme="default"
        />
      )}

      {!loading && !error && chartData.length > 0 && showTable && (
        <TableContainer component={Paper} sx={{ maxHeight: chartHeight, mt: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Timestamp</strong></TableCell>
                {config.tableColumns.map((col, idx) => (
                  <TableCell key={idx} align="left"><strong>{col.header}</strong></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {chartData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{dayjs.utc(row.timestamp).format('MMM DD, YYYY HH:mm')}</TableCell>
                  {config.tableColumns.map((col, idx) => (
                    <TableCell key={idx} align="left">
                      {formatPowerValue(row[col.dataKey])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DasPowerDetectorChart;
